import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Layout } from '../components/Layout'
import { useTokens, useNamespace } from '../lib/hooks'
import { api } from '../lib/api'

interface NewClientSecret {
  id: string
  name: string
  client_id: string
  client_secret: string
}

export function Tokens() {
  const { slug = '' } = useParams()
  const ns     = useNamespace(slug)
  const tokens = useTokens(slug)

  const [showCreate, setShowCreate]       = useState(false)
  const [form, setForm]                   = useState({ name: '', description: '' })
  const [busy, setBusy]                   = useState<string | null>(null)
  const [newSecret, setNewSecret]         = useState<NewClientSecret | null>(null)
  const [feedback, setFeedback]           = useState<{ ok: boolean; msg: string } | null>(null)

  const canAdmin = ['owner', 'admin'].includes(ns.data?.role ?? '')

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    setBusy('create')
    setFeedback(null)
    try {
      const res = await api.post<NewClientSecret>(`/api/namespaces/${slug}/tokens`, form)
      setNewSecret(res)
      setForm({ name: '', description: '' })
      setShowCreate(false)
      tokens.refetch()
    } catch (err) {
      setFeedback({ ok: false, msg: (err as Error).message })
    } finally {
      setBusy(null)
    }
  }

  async function handleRotate(id: string, name: string) {
    if (!window.confirm(`Rotate secret for "${name}"? The current secret will stop working immediately.`)) return
    setBusy(id)
    setFeedback(null)
    try {
      const res = await api.post<{ client_secret: string }>(`/api/namespaces/${slug}/tokens/${id}/rotate`, {})
      setNewSecret({ id, name, client_id: '', client_secret: res.client_secret })
      tokens.refetch()
    } catch (err) {
      setFeedback({ ok: false, msg: (err as Error).message })
    } finally {
      setBusy(null)
    }
  }

  async function handleDelete(id: string, name: string) {
    if (!window.confirm(`Delete bot token "${name}"? This cannot be undone.`)) return
    setBusy(id)
    setFeedback(null)
    try {
      await api.delete(`/api/namespaces/${slug}/tokens/${id}`)
      setFeedback({ ok: true, msg: `Token "${name}" deleted.` })
      tokens.refetch()
    } catch (err) {
      setFeedback({ ok: false, msg: (err as Error).message })
    } finally {
      setBusy(null)
    }
  }

  return (
    <Layout
      title="Bot tokens"
      actions={
        canAdmin && (
          <button className="btn btn-primary" onClick={() => setShowCreate((v) => !v)}>
            {showCreate ? 'Cancel' : '+ New token'}
          </button>
        )
      }
    >
      {/* One-time secret reveal */}
      {newSecret && (
        <div className="card fu" style={{ marginBottom: 24, border: '1px solid rgba(249,115,22,0.3)' }}>
          <div className="card-body">
            <div className="kicker" style={{ marginBottom: 8 }}>Save this secret — shown once</div>
            <div style={{ marginBottom: 12 }}>
              {newSecret.client_id && (
                <div style={{ marginBottom: 8 }}>
                  <div className="field-label">Client ID</div>
                  <div className="code-block" style={{ padding: '8px 12px', fontSize: '0.8rem' }}>
                    {newSecret.client_id}
                  </div>
                </div>
              )}
              <div>
                <div className="field-label">Client Secret</div>
                <div className="code-block" style={{ padding: '8px 12px', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                  {newSecret.client_secret}
                </div>
              </div>
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text3)', marginBottom: 12 }}>
              Use these credentials with your IdP's token endpoint to obtain an access token.
              This secret will <strong style={{ color: 'var(--amber)' }}>never be shown again</strong>.
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => setNewSecret(null)}>
              I've saved it
            </button>
          </div>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <div className="card fu" style={{ marginBottom: 24 }}>
          <div className="card-header"><div className="kicker">New bot token</div></div>
          <div className="card-body">
            <form onSubmit={handleCreate} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label className="field-label">Name</label>
                <input
                  className="input"
                  placeholder="github-ci"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="field-label">Description (optional)</label>
                <input
                  className="input"
                  placeholder="Used by GitHub Actions"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center' }}>
                <button type="submit" className="btn btn-primary" disabled={busy === 'create'}>
                  {busy === 'create' ? <span className="spinner" /> : 'Create'}
                </button>
              </div>
            </form>
            {feedback && !feedback.ok && (
              <div className="feedback-error" style={{ marginTop: 12 }}>{feedback.msg}</div>
            )}
          </div>
        </div>
      )}

      {feedback?.ok && (
        <div className="feedback-success" style={{ marginBottom: 16 }}>{feedback.msg}</div>
      )}

      {tokens.loading && <div className="empty-state">Loading…</div>}
      {tokens.error && (
        <div className="empty-state" style={{ color: 'var(--red)' }}>{tokens.error}</div>
      )}

      {(tokens.data?.tokens.length ?? 0) > 0 && (
        <div className="table-wrap fu">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Auth0 Client ID</th>
                <th>Created</th>
                <th>Last rotated</th>
                {canAdmin && <th></th>}
              </tr>
            </thead>
            <tbody>
              {tokens.data!.tokens.map((t) => (
                <tr key={t.id}>
                  <td>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem' }}>{t.name}</div>
                    {t.description && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--text2)' }}>{t.description}</div>
                    )}
                  </td>
                  <td>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: '0.72rem', color: 'var(--text3)' }}>
                      {t.auth0_client_id}
                    </span>
                  </td>
                  <td style={{ color: 'var(--text2)', fontSize: '0.82rem' }}>
                    {new Date(t.created_at).toLocaleDateString()}
                  </td>
                  <td style={{ color: 'var(--text2)', fontSize: '0.82rem' }}>
                    {t.last_rotated_at ? new Date(t.last_rotated_at).toLocaleDateString() : '—'}
                  </td>
                  {canAdmin && (
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          disabled={busy === t.id}
                          onClick={() => handleRotate(t.id, t.name)}
                        >
                          {busy === t.id ? <span className="spinner" /> : 'Rotate'}
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          disabled={busy === t.id}
                          onClick={() => handleDelete(t.id, t.name)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!tokens.loading && tokens.data?.tokens.length === 0 && (
        <div className="empty-state fu">
          <div className="empty-icon">⬡</div>
          <div>No bot tokens yet.</div>
          {canAdmin && (
            <button
              className="btn btn-ghost btn-sm"
              style={{ marginTop: 4 }}
              onClick={() => setShowCreate(true)}
            >
              Create first token
            </button>
          )}
        </div>
      )}
    </Layout>
  )
}
